import { useCallback, useEffect, useRef, useLayoutEffect } from "react";
import { LocalVideoTrack, LocalAudioTrack } from "twilio-video";
import { i18n } from "@tellescope/i18n";
import { useVideoCalling } from "./useVideoCallingContext";
import type { VideoCallingSlice } from "../types";

/** Track names used to distinguish the shared screen from the camera/microphone on the receiving side. */
export const SCREEN_VIDEO_TRACK_NAME = "screen";
export const SCREEN_AUDIO_TRACK_NAME = "screen-audio";

/** Dismissing the OS screen picker rejects getDisplayMedia; treat it as a no-op, not an error. */
const isUserCancellation = (error: unknown): boolean =>
  error instanceof Error && (error.name === "NotAllowedError" || error.name === "AbortError");

/**
 * Hook for capturing and publishing a screen-share feed (with optional system/tab audio).
 *
 * Captures the screen via `getDisplayMedia`, publishes it as additional tracks named
 * "screen"/"screen-audio" (kept separate from the camera/mic), and tears down cleanly
 * whether the user stops from the in-app control or the browser's native "Stop sharing" bar.
 *
 * Screen-share is a desktop capability — `isScreenShareSupported` is false where
 * `getDisplayMedia` is unavailable (e.g. mobile browsers).
 */
export const useScreenShare = () => {
  const videoCalling = useVideoCalling();

  // Use ref to access latest videoCalling without recreating callbacks on every state change.
  const videoCallingRef = useRef<VideoCallingSlice>(videoCalling);
  useLayoutEffect(() => {
    videoCallingRef.current = videoCalling;
  });

  // Guards teardown against re-entrancy: stopping a track synchronously emits "stopped",
  // which would otherwise re-invoke stopScreenShare while it is already running.
  const tearingDownRef = useRef(false);

  // Probe browser support once on mount.
  useEffect(() => {
    const supported = typeof navigator.mediaDevices?.getDisplayMedia === "function";
    videoCallingRef.current.setScreenShareSupported(supported);
  }, []);

  const stopScreenShare = useCallback(() => {
    if (tearingDownRef.current) return;
    tearingDownRef.current = true;

    const vc = videoCallingRef.current;
    const { localScreenTrack, localScreenAudioTrack } = vc;
    const localParticipant = vc.room?.localParticipant;

    if (localScreenTrack) {
      localParticipant?.unpublishTrack(localScreenTrack);
      localScreenTrack.stop();
    }
    if (localScreenAudioTrack) {
      localParticipant?.unpublishTrack(localScreenAudioTrack);
      localScreenAudioTrack.stop();
    }

    vc.setLocalScreenTrack(null);
    vc.setLocalScreenAudioTrack(null);
    vc.setScreenSharing(false);

    tearingDownRef.current = false;
  }, []);

  const startScreenShare = useCallback(async () => {
    const vc = videoCallingRef.current;
    const localParticipant = vc.room?.localParticipant;
    if (!localParticipant || vc.isScreenSharing) {
      return;
    }

    vc.setScreenShareError(null);

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
    } catch (error) {
      if (!isUserCancellation(error)) {
        console.error("Failed to start screen share:", error);
        vc.setScreenShareError(i18n.t("Couldn't start screen sharing. Please try again.", { ns: "videoCalling" }));
      }
      return;
    }

    const videoMediaTrack = stream.getVideoTracks()[0];
    if (!videoMediaTrack) {
      // getDisplayMedia always yields a video track; guard only to satisfy the type checker.
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    const audioMediaTrack = stream.getAudioTracks()[0];

    const screenVideoTrack = new LocalVideoTrack(videoMediaTrack, { name: SCREEN_VIDEO_TRACK_NAME });
    const screenAudioTrack = audioMediaTrack
      ? new LocalAudioTrack(audioMediaTrack, { name: SCREEN_AUDIO_TRACK_NAME })
      : null;

    // The browser's native "Stop sharing" control ends the underlying media track; mirror
    // that into our state so the UI tears down too.
    screenVideoTrack.once("stopped", stopScreenShare);

    try {
      await localParticipant.publishTrack(screenVideoTrack, { priority: "high" });
      if (screenAudioTrack) {
        await localParticipant.publishTrack(screenAudioTrack);
      }
    } catch (error) {
      console.error("Failed to publish screen share tracks:", error);
      screenVideoTrack.stop();
      screenAudioTrack?.stop();
      vc.setScreenShareError(i18n.t("Couldn't start screen sharing. Please try again.", { ns: "videoCalling" }));
      return;
    }

    vc.setLocalScreenTrack(screenVideoTrack);
    vc.setLocalScreenAudioTrack(screenAudioTrack);
    vc.setScreenSharing(true);
  }, [stopScreenShare]);

  const toggleScreenShare = useCallback(() => {
    if (videoCallingRef.current.isScreenSharing) {
      stopScreenShare();
    } else {
      void startScreenShare();
    }
  }, [startScreenShare, stopScreenShare]);

  return {
    // State (from Zustand)
    isScreenSharing: videoCalling.isScreenSharing,
    isScreenShareSupported: videoCalling.isScreenShareSupported,
    screenShareError: videoCalling.screenShareError,

    // Actions
    startScreenShare,
    stopScreenShare,
    toggleScreenShare,
  };
};
